import React, { useState, useEffect } from 'react';
import { FaFileDownload, FaBuilding } from 'react-icons/fa';
import Swal from 'sweetalert2';
import declaracionesJuradasService from '../../services/declaracionesJuradasService';
import '../../styles/components/DJ1835BienesRaicesTab.css';

/**
 * Componente para visualizar DJ1835 - Bienes Raíces Arrendados
 *
 * Los registros son generados automáticamente al crear la DJ1835
 * desde las tablas arriendos y arriendo_pagos.
 *
 * @param {Object} props
 * @param {number} props.declaracionId - ID de la declaración jurada
 * @param {number} props.year - Año de la declaración
 * @param {number} props.idCompany - ID de la empresa
 * @param {string} props.rutEmpresa - RUT de la empresa
 * @param {string} props.token - Token de autenticación
 */
const DJ1835BienesRaicesTab = ({
    declaracionId,
    year,
    idCompany,
    rutEmpresa,
    token
}) => {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadRegistros = async () => {
            try {
                setLoading(true);
                const data = await declaracionesJuradasService.getBienesRaicesArrendados(
                    declaracionId,
                    idCompany,
                    token
                );
                setRegistros(data);
            } catch (error) {
                console.error('Error al cargar registros:', error);
                Swal.fire({ icon: 'error', title: 'Error', text: 'Error al cargar los registros' });
            } finally {
                setLoading(false);
            }
        };
        loadRegistros();
    }, [declaracionId, idCompany, token]);

    const handleDownloadCSV = async () => {
        if (registros.length === 0) {
            Swal.fire({ icon: 'warning', title: 'Sin datos', text: 'No hay registros para generar el CSV' });
            return;
        }

        try {
            const validation = await declaracionesJuradasService.validateDJ1835Data(
                declaracionId,
                idCompany,
                token
            );

            if (!validation.valid) {
                const result = await Swal.fire({
                    icon: 'warning',
                    title: 'Errores de validación',
                    html: `Se encontraron errores:<br/><ul style="text-align: left;">${validation.errors.map(e => `<li>${e}</li>`).join('')}</ul><br/>¿Desea descargar el CSV de todas formas?`,
                    showCancelButton: true,
                    confirmButtonText: 'Sí, descargar',
                    cancelButtonText: 'Cancelar'
                });

                if (!result.isConfirmed) return;
            }

            await declaracionesJuradasService.downloadAndSaveDJ1835CSV(
                declaracionId,
                idCompany,
                rutEmpresa,
                year,
                token
            );

            Swal.fire({ icon: 'success', title: 'Descarga exitosa', text: 'El archivo CSV se ha descargado correctamente' });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Error al descargar CSV'
            });
        }
    };

    const getMesesSeleccionados = (registro) => {
        const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                       'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
        return meses.filter(mes => registro[mes]).length;
    };

    return (
        <div className="dj1835-tab">
            {/* Header */}
            <div className="tab-header">
                <div className="tab-title">
                    <FaBuilding className="title-icon" />
                    <div>
                        <h2>DJ 1835 - Bienes Raíces Arrendados</h2>
                        <p className="subtitle">Año {year} | Total: {registros.length} registros</p>
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-download"
                        onClick={handleDownloadCSV}
                        disabled={loading || registros.length === 0}
                    >
                        <FaFileDownload /> Descargar CSV
                    </button>
                </div>
            </div>

            {/* Tabla de registros */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-message">Cargando...</div>
                ) : registros.length === 0 ? (
                    <div className="empty-message">
                        <FaBuilding className="empty-icon" />
                        <p>No hay bienes raíces arrendados registrados para este año</p>
                    </div>
                ) : (
                    <table className="registros-table">
                        <thead>
                            <tr>
                                <th>N°</th>
                                <th>ROL Bien Raíz</th>
                                <th>Comuna</th>
                                <th>Cód. Comuna</th>
                                <th>RUT Arrendador</th>
                                <th>RUT Arrendatario</th>
                                <th>Monto Arriendo</th>
                                <th>Meses</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.map((registro) => (
                                <tr key={registro.id}>
                                    <td>{registro.numero}</td>
                                    <td>{registro.rol_bien_raiz}</td>
                                    <td>{registro.comuna_propiedad}</td>
                                    <td>{registro.codigo_comuna || '-'}</td>
                                    <td>{registro.rut_arrendador}</td>
                                    <td>{registro.rut_arrendatario}</td>
                                    <td>${parseInt(registro.monto_arriendo).toLocaleString('es-CL')}</td>
                                    <td>{getMesesSeleccionados(registro)} meses</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DJ1835BienesRaicesTab;
